<template>
  <div class="landing">
    <component
      v-for="s in visibleSections"
      :key="s.key"
      :is="sectionRegistry[s.type]"
      v-bind="sectionProps(s)"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { sectionRegistry } from '@/components/home/sectionRegistry';
import { useTestimonials } from '@/composables/useTestimonials';
import axios from 'axios';

/**
 * Dedicated preview host for the Homepage Builder (portal) — a real,
 * long-lived render of the actual homepage section components, embedded in
 * an iframe. Deliberately NOT a reuse of index.vue: it must never inherit
 * index.vue's auth-dependent branches (ReservationHero/countdown), the
 * builder always previews the guest-facing layout for now (previewing the
 * authenticated-student variant is a follow-up, not built in this pass).
 *
 * Data flow: an initial GET /web/homepage-sections fetch so a bare hit on
 * this URL still shows something real, immediately superseded by
 * `preview-update` postMessages from the builder — see
 * portal/src/composables/useHomepagePreview.js for the other half of this
 * protocol. Every incoming message's origin is validated against
 * `parentOrigin` (read once from this page's own query string) before its
 * payload is trusted; anything else is silently ignored.
 */
const sections = ref([]);
const auth = ref(false);
const user = ref(null);

const { testimonials, testimonialsLoading, fetchTestimonials } = useTestimonials();

const visibleSections = computed(() =>
  sections.value
    .filter((s) => s.is_active && s.type !== 'header' && s.type !== 'footer')
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
);

function sectionProps(s) {
  const settings = s.settings || {};
  switch (s.type) {
    case 'hero':
      return { settings, auth: auth.value, user: user.value };
    case 'quick_access':
      return { settings, auth: auth.value };
    case 'home_products':
      return { settings, kind: settings.kind };
    case 'testimonials':
      return { settings, testimonials: testimonials.value, loading: testimonialsLoading.value };
    default:
      return { settings };
  }
}

let parentOrigin = null;

function isTrustedEvent(event) {
  return !!parentOrigin && event.origin === parentOrigin && event.data?.source === 'homepage-builder';
}

function postToParent(type, extra = {}) {
  if (!parentOrigin || !window.parent) return;
  window.parent.postMessage({ source: 'homepage-builder', type, ...extra }, parentOrigin);
}

function handleMessage(event) {
  if (!isTrustedEvent(event)) return;

  if (event.data.type === 'preview-update') {
    try {
      sections.value = event.data.payload?.sections || [];
    } catch (e) {
      postToParent('preview-error', { message: String(e?.message || e) });
    }
  }
}

async function loadInitialSections() {
  try {
    const res = await axios.get('/web/homepage-sections');
    sections.value = res.data?.data || [];
  } catch (e) {
    sections.value = [];
    postToParent('preview-error', { message: 'تعذّر تحميل بيانات المعاينة الأولية' });
  }
}

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  parentOrigin = params.get('parentOrigin');

  window.addEventListener('message', handleMessage);

  await Promise.all([loadInitialSections(), fetchTestimonials()]);
  await nextTick();

  // Authoritative ready handshake — only sent once the DOM has actually
  // settled with real content, never on a timer/timeout.
  postToParent('preview-ready');
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage);
});
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.landing { font-family: 'Cairo', sans-serif; direction: rtl; overflow-x: hidden; }
</style>
